import { ErrorMessage, Field } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";
import { DEV_MODE } from "../../constants";
import AddressForm from "../form/AddressForm";
import AudioRecordButton from "../form/AudioRecordButton";
import Input from "../form/Input";
import ContactList from "../misc/ContactList";

function CustomerLookupCard({
  icon,
  title,
  fieldPrefix,
  values,
  setFieldValue,
  onSearch,
  isSearching,
  onNumberChange,
  propertyOptions,
  onSelectOption,
  fetchedFields,
  highlightedFields,
  numberLabel,
  nameLabel,
  nameDisabled,
  numberDisabled = false,
  numberCheckbox = null,
  contacts = [],
  searchError = null,
  isLoading = false,
  searchDisabled = false,
  searchDisabledTooltip = "",
}) {
  const { t } = useTranslation();
  const hasMultipleOptions = propertyOptions && propertyOptions.length > 1;
  const hasSingleOption = propertyOptions && propertyOptions.length === 1;

  return (
    <div className="card border-0 mb-4" style={{ position: "relative" }}>
      <div className="card-header h6 mb-0 bg-none p-3">
        <i className={`fa ${icon} fa-lg fa-fw text-dark text-opacity-50 me-1`} /> {title}
        {isLoading && <i className="fa fa-spinner fa-spin ms-2 text-muted" />}
      </div>
      <div className="card-body">
        <div className="row mb-3 gx-3">
          <div className="col-12 col-md-4">
            <div className="d-flex justify-content-between">
              <label className="form-label" htmlFor={`${fieldPrefix}.number`}>
                {numberLabel}
              </label>
              {numberCheckbox && (
                <div className="form-check form-check-reverse">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id={`${fieldPrefix}-number-checkbox`}
                    checked={numberCheckbox.checked}
                    onChange={numberCheckbox.onChange}
                  />
                  <label className="form-check-label form-label" htmlFor={`${fieldPrefix}-number-checkbox`}>
                    {numberCheckbox.label}
                  </label>
                </div>
              )}
            </div>
            {hasMultipleOptions ? (
              <select
                name={`${fieldPrefix}.number`}
                className="form-select fs-13px h-45px"
                style={{
                  borderColor: values.is_fetched ? "#00acac" : undefined,
                }}
                value={values.number || ""}
                disabled={hasSingleOption}
                onChange={(e) => onSelectOption(e.target.value)}
              >
                {propertyOptions.map((opt, idx) => (
                  <option key={idx} value={opt.value.number}>
                    {opt.label}
                  </option>
                ))}
              </select>
            ) : (
              <div className="input-group">
                <Field
                  name={`${fieldPrefix}.number`}
                  component={Input}
                  inputMode="numeric"
                  disabled={hasSingleOption || numberDisabled}
                  className={`form-control fs-13px h-45px ${
                    searchError ? "is-invalid" : values.is_fetched ? "is-valid" : ""
                  }${highlightedFields?.number ? " bg-yellow-100 bg-opacity-25" : ""}`}
                  {...(onNumberChange ? { onChange: onNumberChange } : {})}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && onSearch && !searchDisabled) {
                      e.preventDefault();
                      onSearch();
                    }
                  }}
                />
                {onSearch && (
                  <span className="input-group-text p-0 border-0" title={searchDisabled ? searchDisabledTooltip : ""}>
                    <button
                      type="button"
                      className={`btn btn-${values.is_fetched ? "success" : "default"} h-100`}
                      disabled={isSearching || searchDisabled}
                      onClick={onSearch}
                      style={searchDisabled ? { pointerEvents: "none" } : undefined}
                    >
                      <i className={`fa ${isSearching ? "fa-spinner fa-spin" : "fa-search"}`} />
                    </button>
                  </span>
                )}
              </div>
            )}
            <ErrorMessage
              name={`${fieldPrefix}.number`}
              render={(msg) => <div className="invalid-feedback d-block">{t(msg)}</div>}
            />
            {searchError && <div className="text-danger small mt-1">{t(searchError)}</div>}
          </div>
          <div className="col-12 col-md">
            <label className="form-label" htmlFor={`${fieldPrefix}.name`}>
              {nameLabel}
            </label>
            <div className="input-group">
              <Field
                name={`${fieldPrefix}.name`}
                component={Input}
                className={`form-control fs-13px h-45px ${fetchedFields?.name ? "is-valid" : ""}${
                  highlightedFields?.name ? " bg-yellow-100 bg-opacity-25" : ""
                }`}
                disabled={nameDisabled}
              />
              {!nameDisabled && (
                <AudioRecordButton
                  onTranscription={(text) => {
                    const current = values.name || "";
                    setFieldValue(`${fieldPrefix}.name`, current ? `${current} ${text}` : text);
                  }}
                />
              )}
            </div>
            <ErrorMessage
              name={`${fieldPrefix}.name`}
              render={(msg) => <div className="invalid-feedback d-block">{t(msg)}</div>}
            />
          </div>
        </div>
        <AddressForm
          field={`${fieldPrefix}.addresses`}
          isValid={values.is_fetched}
          isDisabled={values.is_fetched}
          fetchedFields={fetchedFields}
          highlightedFields={highlightedFields}
          addresses={values.addresses}
          setFieldValue={setFieldValue}
        />
        {contacts.length > 0 && (
          <div className="mb-3">
            <div className="form-label">{t("contacts.contacts")}</div>
            <ContactList contacts={contacts} />
          </div>
        )}
      </div>
      {DEV_MODE && (
        <pre>
          {fieldPrefix}: {JSON.stringify(values, null, 2)}
        </pre>
      )}
    </div>
  );
}

export default CustomerLookupCard;
